import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Lock, LogOut, Mail, Loader2, Inbox, ArrowLeft, CheckCheck } from "lucide-react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

export default function Admin() {
  const [authed, setAuthed] = useState(null); // null=checking
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);

  const loadMessages = useCallback(async () => {
    setLoadingMsgs(true);
    try {
      const { data } = await axios.get(`${API}/admin/contacts`);
      setMessages(data);
    } catch {
      toast.error("Failed to load messages.");
    } finally {
      setLoadingMsgs(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        await axios.get(`${API}/auth/me`);
        setAuthed(true);
      } catch {
        setAuthed(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (authed === true) loadMessages();
  }, [authed, loadMessages]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/login`, { email: email.trim(), password });
      setAuthed(true);
      toast.success("Welcome back.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch {
      /* ignore */
    }
    setAuthed(false);
    setEmail("");
    setPassword("");
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/admin/contacts/mark-all-read`);
      setMessages((prev) => prev.map((m) => ({ ...m, read: true })));
      toast.success("All enquiries marked as read.");
    } catch {
      toast.error("Could not update.");
    }
  };

  const unreadCount = messages.filter((m) => !m.read).length;

  if (authed === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505]">
        <Loader2 className="animate-spin text-[#d4af37]" size={32} />
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-[#050505]">
        <motion.form
          data-testid="admin-login-form"
          onSubmit={login}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="glass-card rounded-3xl p-10 w-full max-w-md"
        >
          <div className="flex items-center gap-3 mb-2">
            <span className="w-2 h-2 rotate-45 bg-[#d4af37]" />
            <span className="font-serif-display text-2xl">
              Atelier <span className="gold-text italic">Noir</span>
            </span>
          </div>
          <div className="flex items-center gap-2 text-white/40 text-xs uppercase tracking-[0.3em] mb-8">
            <Lock size={13} /> Admin Access
          </div>

          <label className="text-xs uppercase tracking-[0.2em] text-white/40">Email</label>
          <input
            data-testid="admin-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="admin@ateliernoir.com"
            className="w-full bg-transparent border-b border-white/15 py-4 mb-6 text-lg text-white placeholder-white/30 focus:border-[#d4af37] transition-colors duration-300"
          />
          <label className="text-xs uppercase tracking-[0.2em] text-white/40">Password</label>
          <input
            data-testid="admin-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            className="w-full bg-transparent border-b border-white/15 py-4 mb-8 text-lg text-white placeholder-white/30 focus:border-[#d4af37] transition-colors duration-300"
          />
          <button
            data-testid="admin-login-btn"
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-[#d4af37] text-black font-medium tracking-wide hover:bg-[#f3e5ab] transition-colors duration-300 disabled:opacity-60"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : "Sign In"}
          </button>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 text-white/40 hover:text-white text-sm transition-colors"
          >
            <ArrowLeft size={14} /> Back to site
          </Link>
        </motion.form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] px-6 md:px-12 py-10" data-testid="admin-dashboard">
      <div className="max-w-[1100px] mx-auto">
        <div className="flex items-center justify-between mb-12 pb-8 border-b border-white/10">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="w-2 h-2 rotate-45 bg-[#d4af37]" />
              <span className="font-serif-display text-2xl">
                Atelier <span className="gold-text italic">Noir</span>
              </span>
            </div>
            <p className="text-white/40 text-sm tracking-wide flex items-center gap-2 flex-wrap">
              <Inbox size={14} /> {messages.length} enquir{messages.length === 1 ? "y" : "ies"}
              {unreadCount > 0 && (
                <span
                  data-testid="unread-badge"
                  className="ml-1 inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-[#d4af37] text-black text-xs font-semibold"
                >
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-black/50" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-black" />
                  </span>
                  {unreadCount} new
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {unreadCount > 0 && (
              <button
                data-testid="mark-all-read-btn"
                onClick={markAllRead}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37]/10 transition-colors duration-300 text-sm"
              >
                <CheckCheck size={15} /> Mark all read
              </button>
            )}
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/20 text-white/80 hover:border-[#d4af37] hover:text-[#d4af37] transition-colors duration-300 text-sm"
            >
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>

        {loadingMsgs ? (
          <div className="flex justify-center py-20">
            <Loader2 className="animate-spin text-[#d4af37]" size={28} />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-20 text-white/40">No enquiries yet.</div>
        ) : (
          <div className="flex flex-col gap-4">
            {messages.map((m, i) => (
              <motion.div
                key={m.id}
                data-testid={`admin-message-${i}`}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.03 }}
                className={`rounded-2xl p-6 md:p-8 transition-colors duration-300 ${
                  m.read
                    ? "glass-card"
                    : "bg-[#d4af37]/[0.05] border border-[#d4af37]/30 shadow-[0_8px_32px_rgba(0,0,0,0.5)]"
                }`}
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 mb-4">
                  <div>
                    <h3 className="font-serif-display text-2xl flex items-center gap-3">
                      {!m.read && (
                        <span
                          data-testid={`unread-dot-${i}`}
                          className="w-2 h-2 rounded-full bg-[#d4af37] shrink-0"
                        />
                      )}
                      {m.name}
                      {!m.read && (
                        <span className="px-2 py-0.5 rounded-full bg-[#d4af37] text-black text-[10px] uppercase tracking-wider font-semibold">
                          New
                        </span>
                      )}
                    </h3>
                    <a
                      href={`mailto:${m.email}`}
                      className="text-[#d4af37] text-sm inline-flex items-center gap-1.5 hover:underline"
                    >
                      <Mail size={13} /> {m.email}
                    </a>
                  </div>
                  <div className="text-right">
                    <span className="text-white/40 text-xs">{formatDate(m.created_at)}</span>
                    <span
                      className={`block mt-1 text-[10px] uppercase tracking-wider ${
                        m.email_sent ? "text-emerald-400/70" : "text-red-400/70"
                      }`}
                    >
                      {m.email_sent ? "Emailed" : "Not emailed"}
                    </span>
                  </div>
                </div>
                {m.subject && (
                  <p className="text-white/80 mb-2 font-medium">{m.subject}</p>
                )}
                <p className="text-white/60 leading-relaxed whitespace-pre-wrap">{m.message}</p>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
